// @flow

// eslint-disable ta-code-conventions/has-matching-storydoc

import * as React from 'react';
import { Provider, createClient, dedupExchange, Client, ssrExchange } from 'urql';
import { DenormalizedCacheExchange, useInvalidatableQuery } from './denormalizedCacheExchange';
import { batchFetchExchange } from './batchFetchExchange';
import { DebugExchange } from './debugExchange';
import ssrFetchExchange from './ssrFetchExchange';
import { InitialPageViewMonitorExchange } from './initialPageViewMonitorExchange';

export {
  fulfill as fulfillSsrBatch,
  fulfillData as fulfillSsrBatchData,
  fetchCacheData as fetchSsrCacheData,
  getQueuedRequests as getQueuedSsrRequests,
} from './ssrFetchExchange';

// We want these to be singletons outside of this package because they have state for the debug tool.
const denormalizedCacheExchange = new DenormalizedCacheExchange();
const debugExchange = DEBUG ? new DebugExchange() : null;
export { useInvalidatableQuery, debugExchange, denormalizedCacheExchange };

export type { InvalidationPredicateResponse } from './denormalizedCacheExchange';
export { default as usePaginatedQuery } from './usePaginatedQuery';
export { default as useMutablePaginatedQuery, embedArray } from './useMutablePaginatedQuery';
export { default as useDebouncedQuery } from './useDebouncedQuery';

type UrqlOptions = ?{| securityToken?: string, ssrData?: any, initialPvid: null | string |};
export const buildUrqlClient = (options: UrqlOptions) => {
  const { securityToken, ssrData } = options || {};

  const fetchOptions =
    (securityToken && {
      headers: {
        'X-Requested-By': securityToken,
      },
    }) ||
    {};

  const exchanges = [];
  let pvExchange = null;

  // eslint-disable-next-line ta-code-conventions/lithium-string-blacklist
  if (SERVER) {
    // See note below on why we don't use the dedupe exchange here.
    // A one-shot batcher that builds up requests in the first SSR render pass, storing the results to be consumed by
    // the second render pass.
    exchanges.push(ssrFetchExchange);
  } else {
    if (DEBUG && debugExchange) {
      exchanges.push(debugExchange.exchange.bind(debugExchange));
    }
    pvExchange = new InitialPageViewMonitorExchange(options?.initialPvid || null);
    exchanges.push(
      // Only the client gets the dedup exchange.  The exchange interferes with our two-pass rendering on the server
      // (the first pass returns no OperationResults so the exchange never marks its Operations as completed, and thus
      // "dedupes" all of the second pass' Operations) and doesn't provide much value.
      dedupExchange,
      // This exchange provides insight into when the page is approximately "done loading".
      pvExchange.exchange.bind(pvExchange),
      // Likewise, the cache provides no value to the server because we only actually return cacheable data once, on
      // the final render pass, so there's never a chance to hit it.
      denormalizedCacheExchange.exchange.bind(denormalizedCacheExchange),
      // A static cache of hydrated data from the server.
      ssrExchange({ initialState: ssrData }),
      batchFetchExchange,
    );
  }

  const client = createClient({
    url: `/data/graphql/batched${DEBUG && !securityToken ? '?csrfBypass=true' : ''}`,
    fetchOptions,
    exchanges,
  });
  if (pvExchange) {
    // $FlowFixMe: Exposing this urql state via the client looked like the cleanest option?
    client.pvExchange = pvExchange;
  }

  return client;
};

type Props = {|
  children: React.Node,
  client: Client,
|};

const Urql = ({ children, client }: Props) => <Provider value={client}>{children}</Provider>;

export default Urql;
